import { useCallback, useRef, useState } from 'react';
import axiosInstance from './apiClient';

const useAxios = <T>(
  url: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
  responseType: 'json' | 'blob' = 'json',
  transform?: (data: T) => T,
) => {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const requestIdRef = useRef(0);

  const fetchData = useCallback(
    async (body: any = null) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError(null);
      setErrorStatus(null);
      try {
        const response = await axiosInstance({
          url,
          method,
          data: method === 'GET' ? undefined : body,
          params: method === 'GET' ? body : undefined,
          responseType,
        });

        // Ignore stale responses from older in-flight requests
        if (requestId !== requestIdRef.current) {
          return response.data;
        }

        if (transform) {
          const result = transform(response.data);
          setData(result);
        } else {
          setData(responseType === 'blob' ? (response.data as T) : response.data);
        }

        return response.data; // Return data for immediate use
      } catch (err: any) {
        if (requestId !== requestIdRef.current) {
          return {
            error: err.response?.data?.message || err.message,
            status: err.response?.status,
            isError: true,
          };
        }

        const errorMessage = err.response?.data?.message || err.message;
        const statusCode = err.response?.status;

        setError(errorMessage);
        setErrorStatus(`${statusCode}`);

        return {
          error: errorMessage,
          status: statusCode,
          isError: true,
        };
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [method, responseType, transform, url],
  );

  const resetData = useCallback(() => {
    setData(null);
    setError(null);
    setErrorStatus(null);
  }, []);

  return { data, error, loading, fetchData, resetData, errorStatus };
};

export default useAxios;
